import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2, UsersRound } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HideRetiredFilter } from "@/components/shared/hide-retired-filter";
import { NosLinkCell, NosLinkNote } from "@/components/shared/nos-link-cell";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { nosRowFor, useRoleClassificationNosCheck } from "@/hooks/use-nos-link-check";
import { useRetiredRows } from "@/hooks/use-retired-rows";
import {
  type RoleClassification,
  useCreateRoleClassification,
  useDeleteRoleClassification,
  useReorderRoleClassification,
  useRoleClassifications,
  useUpdateRoleClassification,
} from "@/hooks/use-role-classifications";
import {
  isProtectedRoleClassificationKey,
  type RoleClassificationCreateInput,
  roleClassificationCreateSchema,
} from "@/schemas/role-classifications";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; classification: RoleClassification }
  | { type: "delete"; classification: RoleClassification };

/**
 * Settings → Config → Role classifications: the org-level classification a
 * person is placed in (Individual Contributor / Manager / Leadership / Officer
 * at the time of writing).
 *
 * The one thing this page has that Employment types doesn't is the NOS name
 * column — NOS keeps its own classification lookup and matches on the name, so
 * a classification with no NOS name can't be written back. Surfacing it here is
 * what stops that turning into a mystery failure in the NOS sync log.
 */
export function SettingsRoleClassificationsPage() {
  const { data: rows = [], isLoading, isError, error, refetch } = useRoleClassifications();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const deleteRow = useDeleteRoleClassification();
  const reorder = useReorderRoleClassification();
  const { hideRetired, setHideRetired, active, hiddenCount, visible } = useRetiredRows(rows);

  // Fetched separately from the list so the page renders from the database
  // straight away and the NOS column fills in when the gateway answers.
  const { data: nosCheck, isLoading: nosLoading } = useRoleClassificationNosCheck();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load role classifications"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        The org-level classification a person is placed in, chosen on their profile and tracked over
        time on their history. The order here is the order they're offered in. Retiring one hides it
        from the pickers but keeps it readable on anyone who already has it and in their history — a
        classification can only be deleted while nobody holds it.{" "}
        <span className="font-medium text-foreground">The NOS name</span> is how this classification
        is matched in NOS, which spells some of them differently (its "Individual Contributors" is
        our "Individual Contributor"). Leave it blank and people with this classification can't be
        sent to NOS until it's filled in.
      </p>

      <NosLinkNote check={nosCheck} loading={nosLoading} noun="classifications" />

      <DataToolbar
        filters={
          <HideRetiredFilter
            id="role-classifications-hide-retired"
            checked={hideRetired}
            onCheckedChange={setHideRetired}
            hiddenCount={hiddenCount}
          />
        }
        actions={
          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add classification
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Order", className: "w-24" },
          { label: "Name" },
          { label: "NOS name" },
          { label: "NOS ID" },
          { label: "People" },
          { label: "Status" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={UsersRound}
            title="No role classifications"
            description="The four defaults are seeded on server startup."
          />
        }
      >
        {visible.map((row, index) => (
          <tr
            key={row.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              {/* A retired row is pinned to the bottom by the API ordering, so its
                  position carries no meaning and it gets no arrows. The arrows on
                  the active rows above it are therefore always adjacent swaps. */}
              {row.isActive ? (
                <ReorderButtons
                  label={row.name}
                  disableUp={index === 0 || reorder.isPending}
                  disableDown={index === active.length - 1 || reorder.isPending}
                  onMove={(direction) => reorder.mutate({ id: row.id, direction })}
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="font-medium">{row.name}</div>
              {/* The key is what the rest of the system stores and what every
                  past change record holds, so it is shown but never editable. */}
              <code className="text-xs text-muted-foreground">{row.key}</code>
            </td>
            <td className="px-4 py-3">
              {row.nosName ? (
                <span className="text-sm">{row.nosName}</span>
              ) : (
                <StatusPill tone="warning" label="Not set" />
              )}
            </td>
            <td className="px-4 py-3">
              <NosLinkCell
                check={nosCheck}
                row={nosRowFor(nosCheck, row.id)}
                loading={nosLoading}
              />
            </td>
            <td className="px-4 py-3 text-muted-foreground">{row.personCount}</td>
            <td className="px-4 py-3">
              <StatusPill
                tone={row.isActive ? "success" : "neutral"}
                label={row.isActive ? "Active" : "Inactive"}
              />
            </td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[
                  {
                    icon: Pencil,
                    label: "Edit",
                    onClick: () => setDialog({ type: "edit", classification: row }),
                  },
                  {
                    icon: Trash2,
                    label: "Delete",
                    onClick: () => setDialog({ type: "delete", classification: row }),
                    variant: "destructive",
                    separatorBefore: true,
                    // Built-in classifications and ones anyone still holds can
                    // only be deactivated — the server refuses either way, but
                    // there is no point offering an action that can't succeed.
                    disabled: isProtectedRoleClassificationKey(row.key) || row.personCount > 0,
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {(dialog.type === "create" || dialog.type === "edit") && (
        <RoleClassificationDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          classification={dialog.type === "edit" ? dialog.classification : undefined}
        />
      )}

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title={`Delete "${dialog.classification.name}"?`}
          description="This removes the classification from the list. It can only be deleted while nobody holds it — otherwise deactivate it instead, which hides it from the pickers but keeps it readable on past records."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteRow.mutate(dialog.classification.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

function RoleClassificationDialog({
  open,
  onOpenChange,
  classification,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classification?: RoleClassification;
}) {
  const isEdit = classification != null;
  const create = useCreateRoleClassification();
  const update = useUpdateRoleClassification(classification?.id ?? "");
  const form = useForm<RoleClassificationCreateInput>({
    resolver: zodResolver(roleClassificationCreateSchema),
    defaultValues: {
      key: classification?.key ?? "",
      name: classification?.name ?? "",
      nosName: classification?.nosName ?? null,
      isActive: classification?.isActive ?? true,
    },
  });

  async function onSubmit(values: RoleClassificationCreateInput) {
    if (isEdit) {
      // `key` is deliberately not sent: it is immutable once created, because
      // it is stored on every person's record, on their history spans, and in
      // every past change record.
      await update.mutateAsync({
        name: values.name,
        nosName: values.nosName ?? null,
        isActive: values.isActive,
      });
    } else {
      await create.mutateAsync(values);
    }
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit role classification" : "Add role classification"}
      description={
        isEdit
          ? "Update the label, the NOS name, or whether this classification can still be chosen."
          : "Add a new org-level classification people can be placed in."
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Create"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Team Lead" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="key"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Key</FormLabel>
            <FormControl>
              <Input placeholder="e.g. team_lead" {...field} disabled={isEdit} />
            </FormControl>
            <FormDescription>
              {isEdit
                ? "The key can't be changed — it's stored on every person's record and in their change history."
                : "A permanent identifier used internally. Lowercase letters, numbers and underscores."}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="nosName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>NOS name</FormLabel>
            <FormControl>
              <Input
                placeholder="Exactly as NOS spells it"
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
              />
            </FormControl>
            <FormDescription>
              Must match NOS exactly. Leave blank if NOS has no equivalent — people with this
              classification then can't be sent to NOS until it's filled in.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isActive"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value ?? true} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Available for selection</FormLabel>
              <FormDescription>
                Turn this off to retire the classification. It disappears from the pickers but keeps
                rendering in history and on anyone who still holds it.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
